/* eslint-disable max-len */
import axios from 'axios';
import config from '../config/index.js';
import { authHeader } from '../redux/helper.jsx';

// Inject seller_email for super-admin impersonation on every request
// Backend resolve_seller_email checks queryStringParameters first, so query params work for all methods
axios.interceptors.request.use(requestConfig => {
    const sellerEmail = sessionStorage.getItem('selectedSellerEmail');
    if (!sellerEmail) return requestConfig;

    // Don't inject seller_email into super-admin session lock API calls
    if (requestConfig.url?.includes('/super-admin/session')) return requestConfig;

    requestConfig.params = { ...requestConfig.params, seller_email: sellerEmail };
    return requestConfig;
});

/**
 * The function VerifyRecaptcha is an asynchronous function that sends a POST request to verify a
 * reCAPTCHA token and returns an object with an error flag and the response data.
 * @param token - The `token` parameter is a string that represents the user's response to the
 * reCAPTCHA challenge. It is typically generated by the reCAPTCHA widget on the client-side and passed
 * to the server for verification.
 * @returns an object with two properties: "error" and "data". The "error" property indicates whether
 * an error occurred during the execution of the function. If an error occurred, the value of "error"
 * will be true. The "data" property contains the response data from the API call.
 */
async function VerifyRecaptcha(token) {
    try{
        const data = await axios.post(`${config.API.baseURL}/users-management/verify-captcha`, token)
        return { error: false, data: data };
    } catch (error) {
        return { error: true, data: error.response };
    }
}

/**
 * The function `VerifyOtp` is an asynchronous function that sends a POST request to a specified API
 * endpoint for OTP validation and returns the response data or an error object.
 * @param body - The `body` parameter is an object that contains the data to be sent in the request
 * body when making a POST request to the OTP validation endpoint. It should include the necessary
 * information required for OTP validation, such as the user's phone number and the OTP code.
 * @returns The function `VerifyOtp` is returning an object with two properties: `error` and `data`.
 * The `error` property indicates whether an error occurred during the execution of the function. If
 * `error` is `false`, it means that the OTP validation was successful and the `data` property contains
 * the response data from the API call. If `error` is `true`, it means
 */
async function VerifyOtp(body) {
    try{
        const data = await axios.post(`${config.API.baseURL}/users-management/otp-validation`, body)
        return { error: false, data: data };
    } catch (error) {
        return { error: true, data: error.response };
    }
}
async function ViewProfile(body) {
    const headers= await authHeader();
    try{
        const data = await axios.get(`${config.API.baseURL}/users-management/${body.email}`,{
            headers: headers,
        })
        return { error: false, data: data };
    } catch (error) {
        return { error: true, data: error.response };
    }
}
async function UpdateProfile(body) {
    const headers= await authHeader();
    try{
        const data = await axios.patch(`${config.API.baseURL}/users-management/${body.email_address}`,body,{
            headers: headers,
        })
        return { error: false, data: data };
    } catch (error) {
        return { error: true, data: error.response };
    }
}
async function ResetPasswordLink(body){
    try{
        const data = await axios.post(`${config.API.baseURL}/users-management/forgot_password`, body)
        return { error: false, data: data.data };
    }
    catch (error) {
        return { error: true, message: error };
    }
}
async function ConfirmPassword(body){
    try{
        const data = await axios.post(`${config.API.baseURL}/users-management/reset_password`, body)
        return { error: false, data: data.data }
    }
    catch(error){
        return { error: true, data: error.response };
    }
}
async function UpdatePassword(email_address, body) {
    const headers= await authHeader();
    try{
        const data = await axios.patch(`${config.API.baseURL}/users-management/password-update/${email_address}`, body, {
            headers: headers,
        })
        return { error: false, data: data };
    } catch (error) {
        return { error: true, data: error.response };
    }
}
async function CreatePassword( body ) {
    const headers= await authHeader();
    try{
        const data = await axios.post(`${config.API.baseURL}/users-management/set-password`, body, {
            headers: headers,
        })
        return { error: false, data: data };
    } catch (error) {
        return { error: true, data: error.response };
    }
}
async function getKycToken() {
    const headers= await authHeader();
    try {
        const category = await axios.get(`${config.API.baseURL}/users-management/generate`, {
            headers: headers,
        });
        return { error: false, data: category.data };
    } catch (e) {
        return { error: true, message: e };
    }
}
async function getKybToken() {
    const headers= await authHeader();
    try {
        const category = await axios.get(`${config.API.baseURL}/users-management/kyb-generate`, {
            headers: headers,
        });
        return { error: false, data: category.data };
    } catch (e) {
        return { error: true, message: e };
    }
}
async function updatePricing(email_address,body) {
    const headers= await authHeader();
    try {
        const category = await axios.patch(`${config.API.baseURL}/users-management/update-plan/${email_address}`, body,{
            headers: headers,
        });
        return { error: false, data: category.data };
    } catch (e) {
        return { error: true, message: e };
    }
}
async function connectStripe() {
    const headers= await authHeader();
    try {
        const response = await axios.get(`${config.API.baseURL}/users-management/stripe`,{
            headers: headers,
        });
        return { error: false, data: response.data };
    } catch (e) {
        return { error: true, message: e };
    }
}
async function disconnectStripe(body) {
    const headers= await authHeader();
    try {
        const response = await axios.patch(`${config.API.baseURL}/users-management/stripe`,body,{
            headers: headers,
        });
        return { error: false, data: response.data };
    } catch (e) {
        return { error: true, message: e };
    }
}

async function connectPaypal() {
    const headers= await authHeader();
    try {
        const response = await axios.get(`${config.API.baseURL}/paypal/paypal-connect`,{
            headers: headers,
        });
        return { error: false, data: response.data };
    } catch (e) {
        return { error: true, message: e };
    }
}

async function disconnectPaypal(body) {
    const headers= await authHeader();
    try {
        const response = await axios.patch(`${config.API.baseURL}/paypal/paypal-disconnect`,body,{
            headers: headers,
        });
        return { error: false, data: response.data };
    } catch (e) {
        return { error: true, message: e };
    }
}

async function AuctionList(endpoint) {
    const headers= await authHeader();
    try {
        const response = await axios.get(`${config.API.baseURL}/auctions${endpoint}`,{
            headers: headers,
        });
        return { error: false, data: response.data };
    } catch (e) {
        return { error: true, message: e };
    }
}
async function AddNotes(endpoint,body) {
    const headers= await authHeader();
    try {
        const response = await axios.patch(`${config.API.baseURL}/auctions${endpoint}`,body,{
            headers: headers,
        });
        return { error: false, data: response.data };
    } catch (e) {
        return { error: true, message: e };
    }
}

async function CreateAuction(body) {
    const headers= await authHeader();
    try {
        const response = await axios.post(`${config.API.baseURL}/auctions`,body,{
            headers: headers,
        });
        return { error: false, data: response.data };
    } catch (e) {
        return { error: true, message: e };
    }
}

async function LotList(endpoint, live=false) {
    const headers= await authHeader();
    try {
        const response = await axios.get(`${config.API.baseURL}/${live ? 'in-person-auction/list-lots' : 'auctions/lots'}${endpoint}`,{
            headers: headers,
        });
        return { error: false, data: response.data };
    } catch (e) {
        return { error: true, message: e };
    }
}
async function viewAuction(endpoint) {
    const headers= await authHeader();
    try {
        const response = await axios.get(`${config.API.baseURL}/auctions/view${endpoint}`,{
            headers: headers,
        });
        return { error: false, data: response.data };
    } catch (e) {
        return { error: true, message: e };
    }
}
async function editAuction(id,body) {
    const headers= await authHeader();
    try {
        const response = await axios.patch(`${config.API.baseURL}/auctions/update/${id}`,body,{
            headers: headers,
        });
        return { error: false, data: response.data };
    } catch (e) {
        return { error: true, message: e.response };
    }
}

async function CreateLot(body, live=false) {
    const headers= await authHeader();
    try {
        const response = await axios.post(`${config.API.baseURL}/${live ? 'in-person-auction' : 'auctions'}/lots`,body,{
            headers: headers,
        });
        return { error: false, data: response.data };
    } catch (e) {
        return { error: true, message: e };
    }
}
async function ImportLot(body) {
    const headers= await authHeader();
    try {
        const response = await axios.post(`${config.API.baseURL}/auctions/import`,body,{
            headers: headers,
        });
        return { error: false, data: response.data };
    } catch (error) {
        return { error: true, data: error.response };
    }
}

async function deleteLot(body, live=false) {
    const headers= await authHeader();
    try {
        const response = await axios.delete(`${config.API.baseURL}/${live ? 'in-person-auction' : 'auctions'}/lots`,{
            headers: headers,
            data: body
        });
        return { error: false, data: response.data };
    } catch (e) {
        return { error: true, message: e.response };
    }
}

async function updateLot(body, live=false) {
    const headers= await authHeader();
    try {
        const response = await axios.patch(`${config.API.baseURL}/${live ? 'in-person-auction' : 'auctions'}/lots`,body,{
            headers: headers,
        });
        return { error: false, data: response.data };
    } catch (e) {
        return { error: true, message: e };
    }
}

async function CloneAuction(body) {
    const headers= await authHeader();
    try {
        const response = await axios.post(`${config.API.baseURL}/auctions/clone`,body,{
            headers: headers,
        });
        return { error: false, data: response.data };
    } catch (e) {
        return { error: true, message: e };
    }
}

async function DeleteAuction(id, live=false) {
    const headers= await authHeader();
    try {
        const response = await axios.delete(`${config.API.baseURL}/${live ? 'in-person-auction' : 'auctions'}/${id}`,{
            headers: headers,
        });
        return { error: false, data: response.data };
    } catch (e) {
        return { error: true, message: e.response };
    }
}
async function publishAuction(id) {
    const headers= await authHeader();
    try {
        const response = await axios.patch(`${config.API.baseURL}/auctions/update/${id}?published=true`,{},{
            headers: headers
        });
        return { error: false, data: response.data };
    } catch (e) {
        return { error: true, message: e };
    }
}

async function UnpublishAuction(auction_id,body) {
    const headers= await authHeader();
    try {
        const response = await axios.patch(`${config.API.baseURL}/auctions/${auction_id}`,body,{
            headers: headers
        });
        return { error: false, data: response.data };
    } catch (e) {
        return { error: true, message: e };
    }
}

async function reorderLots(body) {
    const headers = await authHeader();
    try {
        const response = await axios.post(`${config.API.baseURL}/auctions/reorder-lots`, body, {
            headers: headers
        });
        return { error: false, data: response.data };
    } catch (e) {
        console.error('Error reordering lots:', e);
        return { error: true, message: e.response?.data?.message || 'Failed to reorder lots' };
    }
}

// Keep the existing single lot reorder method as backup
async function reorderLot(body) {
    const headers = await authHeader();
    try {
        const response = await axios.post(`${config.API.baseURL}/auctions/reorder`, body, {
            headers: headers
        });
        
        if (response.status === 204) {
            return { error: false, data: {} };
        }
        return { error: false, data: response.data };
    } catch (e) {
        console.error('Error reordering lot:', e);
        return { error: true, message: e.response?.data?.message || 'Failed to reorder lot' };
    }
}

async function BidderList(endpoint) {
    const headers= await authHeader();
    try {
        const response = await axios.get(`${config.API.baseURL}/bids${endpoint}`,{
            headers: headers,
        });
        return { error: false, data: response.data };
    } catch (e) {
        return { error: true, message: e };
    }
}
async function handleBidders(endpoint) {
    const headers= await authHeader();
    try {
        const response = await axios.patch(`${config.API.baseURL}/buyers/approval${endpoint}`,{},{
            headers: headers,
        });
        return { error: false, data: response.data };
    } catch (e) {
        return { error: true, message: e };
    }
}

async function SubDomainSetup(endpoint,body) {
    const headers= await authHeader();
    try {
        const response = await axios.patch(`${config.API.baseURL}/subdomain/subdomain${endpoint}`,body,{
            headers: headers,
        });
        return { error: false, data: response.data };
    } catch (e) {
        return { error: true, message: e };
    }
}

async function BidderDetails(endpoint) {
    const headers= await authHeader();
    try {
        const response = await axios.get(`${config.API.baseURL}/bids/${endpoint}`,{
            headers: headers,
            
        });
        return { error: false, data: response.data };
    } catch (e) {
        return { error: true, message: e };
    }
}
async function SalesList(endpoint) {
    const headers= await authHeader();
    try {
        const response = await axios.get(`${config.API.baseURL}/orders/seller${endpoint}`,{
            headers: headers,
        });
        return { error: false, data: response.data };
    } catch (e) {
        return { error: true, message: e };
    }
}

async function getOrderDetails(id) {
    const headers= await authHeader();
    try {
        const response = await axios.get(`${config.API.baseURL}/orders/sales?order_id=${id}`,{
            headers: headers,
        });
        return { error: false, data: response.data };
    } catch (e) {
        return { error: true, message: e };
    }
}

async function getAllBidsOfLot(id,endpoint) {
    const headers= await authHeader();
    try {
        const response = await axios.get(`${config.API.baseURL}/lot-bid-history/${id}?${endpoint}`,{
            headers: headers,
        });
        return { error: false, data: response.data };
    } catch (e) {
        return { error: true, message: e };
    }
}

async function AllBidderList(endpoint) {
    const headers= await authHeader();
    try {
        const response = await axios.get(`${config.API.baseURL}/seller-bidder-management${endpoint}`,{
            headers: headers,
        });
        return { error: false, data: response.data };
    } catch (e) {
        return { error: true, message: e };
    }
}

async function AllSalesList(endpoint) {
    const headers= await authHeader();
    try {
        const response = await axios.get(`${config.API.baseURL}/seller-bidder-management/seller-orders${endpoint}`,{
            headers: headers,
        });
        return { error: false, data: response.data };
    } catch (e) {
        return { error: true, message: e };
    }
}

async function getAllBids(id,endpoint) {
    const headers= await authHeader();
    try {
        // eslint-disable-next-line max-len
        const response = await axios.get(`${config.API.baseURL}/lot-bid-history/seller/bids?auction_id=${id}&${endpoint}`,{
            headers: headers,
        });
        return { error: false, data: response.data };
    } catch (e) {
        return { error: true, message: e };
    }
}

async function changePaymentInfo(orderId, {paymentType, paymentStatus}) {
    const headers = await authHeader();
    try {
        const response = await axios.put(`${config.API.baseURL}/orders/update`, {
            order_id: orderId,
            payment_status: paymentStatus,
            payment_type: paymentType
        }, {
            headers
        })
        return { error: false, res: response }
    } catch (e) {
        return { error: true, message: e }
    }
}

async function sendPaymentReminder(orderId, emailAddress = null) {
    const headers = await authHeader();
    try {
        const body = {
            order_id: orderId
        };
        if (emailAddress) {
            body.email_address = emailAddress;
        }
        const response = await axios.post(`${config.API.baseURL}/orders/send-payment-reminder`, body, {
            headers
        })
        return { error: false, data: response.data }
    } catch (e) {
        return { error: true, message: e.response }
    }
}

async function BannerNotification() {
    try {
        const response = await axios.get(`${config.API.baseURL}/site-banner/${encodeURIComponent("All Sellers")}`);
        return { error: false, data: response.data };
    } catch (e) {
        return { error: true, message: e };
    }
}

async function NewsNotication(body) {
    const headers= await authHeader();
    try {
        const response = await axios.patch(`${config.API.baseURL}/newsletter`,body,{
            headers: headers,
        });
        return { error: false, data: response.data };
    } catch (e) {
        return { error: true, message: e };
    }
}


async function Accesslogs() {
    const headers= await authHeader();
    try{
        const data = await axios.post(`${config.API.baseURL}/users-management/save-log`,{},{
            headers: headers,
        })
        return { error: false, data: data };
    } catch (error) {
        return { error: true, data: error.response };
    }
}

async function getQuickSightEmbedUrl() {
    const headers= await authHeader();
    try{
        const data = await axios.get(`${config.API.baseURL}/quicksight-dashboards`,{
            headers: headers,
        })
        return { error: false, data: data };
    } catch (error) {
        return { error: true, data: error.response };
    }
}

async function AuctionInsights(auction_id) {
    const headers= await authHeader();
    try{
        const data = await axios.get(`${config.API.baseURL}/quicksight-dashboards/auction-view?auction_id=${auction_id}`,{
            headers: headers,
        })
        return { error: false, data: data };
    } catch (error) {
        return { error: true, data: error.response };
    }
}

async function GetTemplate(name, default_name) {
    const headers= await authHeader();
    try{
        const data = await axios.get(`${config.API.baseURL}/users-management/get-template/${name}?default_template=${default_name}`,{
            headers: headers,
        })
        return { error: false, data: data };
    } catch (error) {
        return { error: true, data: error.response };
    }
}

async function updateTemplate(action, body) {
    const headers= await authHeader();
    try{
        const data = await axios.post(`${config.API.baseURL}/users-management/create-template?action=${action}`,body,{
            headers: headers,
        })
        return { error: false, data: data };
    } catch (error) {
        return { error: true, data: error.response };
    }
}

async function getVersion(body) {
    try{
        const data = await axios.post(`${process.env.REACT_APP_VERSION_API_BASE_URL}`,body)
        return { error: false, data: data };
    } catch (error) {
        return { error: true, data: error.response };
    }
}

async function CreateLiveAuction(body) {
    const headers= await authHeader();
    try {
        const response = await axios.post(`${config.API.baseURL}/in-person-auction/`,body,{
            headers: headers,
        });
        return { error: false, data: response.data };
    } catch (e) {
        return { error: true, message: e };
    }
}

async function EditLiveAuction(auction_id,body) {
    const headers= await authHeader();
    try {
        const response = await axios.patch(`${config.API.baseURL}/in-person-auction/${auction_id}`,body,{
            headers: headers,
        });
        return { error: false, data: response.data };
    } catch (e) {
        return { error: true, message: e.response };
    }
}

async function PublishLiveAuction(body) {
    const headers= await authHeader();
    try {
        const response = await axios.patch(`${config.API.baseURL}/in-person-auction/publish`,body,{
            headers: headers,
        });
        return { error: false, data: response.data };
    } catch (e) {
        return { error: true, message: e };
    }
}

async function UnpublishLiveAuction(body) {
    const headers= await authHeader();
    try {
        const response = await axios.patch(`${config.API.baseURL}/in-person-auction/unpublish`,body,{
            headers: headers,
        });
        return { error: false, data: response.data };
    } catch (e) {
        return { error: true, message: e };
    }
}

async function CompleteAuction(body) {
    const headers= await authHeader();
    try {
        const response = await axios.patch(`${config.API.baseURL}/in-person-auction/auction-complete`,body,{
            headers: headers,
        });
        return { error: false, data: response.data };
    } catch (e) {
        return { error: true, message: e.response };
    }
}

async function GetLeaderboard(auction_id) {
    const headers= await authHeader();
    try{
        const data = await axios.get(`${config.API.baseURL}/auctions/leaderboard/${auction_id}`,{
            headers: headers,
        })
        return { error: false, data: data };
    } catch (error) {
        return { error: true, data: error.response };
    }
}

async function GetLiveAuctionBids(auction_id, bid_type, endpoint) {
    const headers= await authHeader();
    try {
        const response = await axios.get(`${config.API.baseURL}/in-person-auction/bids?auction_id=${auction_id}&bid_type=${bid_type}&${endpoint}`,{
            headers: headers,
        });
        return { error: false, data: response.data };
    } catch (e) {
        return { error: true, message: e };
    }
}

async function deleteBid(bid_id) {
    const headers= await authHeader();
    try {
        const response = await axios.delete(`${config.API.baseURL}/in-person-auction/delete-bid/${bid_id}`,{
            headers: headers,
        });
        return { error: false, data: response.data };
    } catch (e) {
        return { error: true, message: e.response };
    }
}

async function CancelAuction(body) {
    const headers= await authHeader();
    try {
        const response = await axios.patch(`${config.API.baseURL}/in-person-auction/cancel-auction`,body,{
            headers: headers,
        });
        return { error: false, data: response.data };
    } catch (e) {
        return { error: true, message: e.response };
    }
}

async function AuctionStats(body) {
    const headers= await authHeader();
    try {
        const response = await axios.get(`${config.API.baseURL}/in-person-auction/auction-statistics?auction_id=${body.auction_id}`,{
            headers: headers,
        });
        return { error: false, data: response.data };
    } catch (e) {
        return { error: true, message: e.response };
    }
}

async function deleteImageFromS3(body) {
    const headers= await authHeader();
    try {
        const response = await axios.delete(`${config.API.baseURL}/auctions/image/`,{
            headers: headers,
            data: body
        });
        return { error: false, data: response.data };
    } catch (e) {
        return { error: true, message: e.response };
    }
}

// Super-admin session lock APIs
async function acquireSuperAdminSession(sellerEmail) {
    const headers = await authHeader();
    try {
        const response = await axios.post(`${config.API.baseURL}/users-management/super-admin/session`, { seller_email: sellerEmail }, {
            headers: headers,
        });
        return { error: false, data: response.data };
    } catch (e) {
        return { error: true, status: e.response?.status, data: e.response?.data };
    }
}

async function releaseSuperAdminSession(sellerEmail) {
    const headers = await authHeader();
    try {
        const response = await axios.delete(`${config.API.baseURL}/users-management/super-admin/session`, {
            headers: headers,
            params: { seller_email: sellerEmail },
        });
        return { error: false, data: response.data };
    } catch (e) {
        return { error: true, data: e.response?.data };
    }
}

async function getSuperAdminSession(sellerEmail) {
    const headers = await authHeader();
    try {
        const params = sellerEmail ? { seller_email: sellerEmail } : {};
        const response = await axios.get(`${config.API.baseURL}/users-management/super-admin/session`, {
            headers: headers,
            params: params,
        });
        return { error: false, data: response.data };
    } catch (e) {
        return { error: true, data: e.response?.data };
    }
}

async function ViewLot(auction_id, lot_number) {
    const headers= await authHeader();
    try {
        const response = await axios.get(`${config.API.baseURL}/auctions/lots/${auction_id}/${lot_number}`,{
            headers: headers,
        });
        return { error: false, data: response.data };
    } catch (e) {
        return { error: true, message: e.response };
    }
}

async function processPayment(orderId) {
    const headers = await authHeader();
    const body = {
        order_id: orderId
    };
    try {
        const response = await axios.post(`${config.API.baseURL}/payments/process-stored-card`, body, {
            headers: headers,
        });
        return { error: false, data: response.data };
    } catch (e) {
        return {error: true, message: e.response}
    }
}

async function sendPaymentRequest(body) {
    const headers = await authHeader();
    try {
        const response = await axios.post(`${config.API.baseURL}/orders/send-payment-request`, body, {
            headers
        })
        return { error: false, data: response.data }
    } catch (e) {
        return { error: true, message: e.response }
    }
}

export const dataService = {
    VerifyRecaptcha,
    VerifyOtp,
    ViewProfile,
    UpdateProfile,
    ResetPasswordLink,
    ConfirmPassword,
    UpdatePassword,
    CreatePassword,
    getKycToken,
    updatePricing,
    getKybToken,
    connectStripe,
    disconnectStripe,
    AuctionList,
    AddNotes,
    CreateAuction,
    LotList,
    CreateLot,
    reorderLot,
    reorderLots,
    viewAuction,
    ImportLot,
    deleteLot,
    updateLot,
    editAuction,
    CloneAuction,
    DeleteAuction,
    publishAuction,
    BidderList,
    handleBidders,
    SubDomainSetup,
    BidderDetails,
    SalesList,
    getOrderDetails,
    getAllBidsOfLot,
    getAllBids,
    AllBidderList,
    UnpublishAuction,
    changePaymentInfo,
    sendPaymentReminder,
    AllSalesList,
    BannerNotification,
    NewsNotication,
    connectPaypal,
    disconnectPaypal,
    Accesslogs,
    getQuickSightEmbedUrl,
    AuctionInsights,
    GetTemplate,
    updateTemplate,
    getVersion,
    CreateLiveAuction,
    EditLiveAuction,
    PublishLiveAuction,
    UnpublishLiveAuction,
    CompleteAuction,
    GetLeaderboard,
    GetLiveAuctionBids,
    deleteBid,
    CancelAuction,
    AuctionStats,
    deleteImageFromS3,
    ViewLot,
    processPayment,
    sendPaymentRequest,
    acquireSuperAdminSession,
    releaseSuperAdminSession,
    getSuperAdminSession
}